import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input, InputGroup } from "@/components/ui/Input";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { TextareaGroup } from "@/components/ui/Textarea";
import { toast } from "@/components/ui/Toast";
import { useTranslation } from "@/hooks/useTranslation";
import { api } from "@/lib/api-client";

// ─────────────────────────────────────────────
// SchemaEdit — FIX-016 / ITEM-019d
//
// Full schema editor for DRAFT schema versions.
// Route: /schemas/:id/versions/:vId/edit
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────

type QuestionType =
  | "WEIGHTED_MULTIPLE_CHOICE"
  | "WEIGHTED_YES_NO"
  | "MULTI_SUB_FIELD"
  | "OPEN_ENDED";

// Internal option representation used in both Add and Edit dialogs.
// Converted to the correct API format on submit.
interface QuestionOption {
  key: string;   // e.g. "fully_implemented" or "yes"
  label: string; // e.g. "Fully Implemented" or "Yes"
  score: number; // 0-100
}

// MULTI_SUB_FIELD: a question splits into multiple sub-fields (e.g. CIA triad),
// each with its own list of scored options.
interface QuestionSubField {
  key: string;
  label: string;
  options: QuestionOption[];
}


interface Question {
  id: string;
  code: string;
  text: string;
  type: QuestionType;
  weight: number;
  required: boolean;
  sortOrder: number;
  domainId: string;
  postureId: string;
  options?: QuestionOption[] | null;
}

interface Domain {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
}

interface Posture {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  weight: number;
  domainId?: string | null;
  questions: Question[];
}

interface SchemaVersionDetail {
  id: string;
  schemaId: string;
  version: number;
  status: string;
  domains: Domain[];
  postures: Posture[];
}

interface SchemaRecord {
  id: string;
  name: string;
  description?: string | null;
  type: string;
  isPreset?: boolean;
}

const QUESTION_TYPE_OPTIONS: { value: QuestionType; labelKey: string }[] = [
  { value: "WEIGHTED_MULTIPLE_CHOICE", labelKey: "schema.edit.typeWeightedMc" },
  { value: "WEIGHTED_YES_NO", labelKey: "schema.edit.typeWeightedYesNo" },
  { value: "MULTI_SUB_FIELD", labelKey: "schema.edit.typeMultiSubField" },
  { value: "OPEN_ENDED", labelKey: "schema.edit.typeOpenEnded" },
];

// ── Option defaults ───────────────────────────

const DEFAULT_MC_OPTIONS: QuestionOption[] = [
  { key: "not_implemented", label: "Not Implemented", score: 0 },
  { key: "partially_implemented", label: "Partially Implemented", score: 50 },
  { key: "fully_implemented", label: "Fully Implemented", score: 100 },
];

const DEFAULT_YES_NO_OPTIONS: QuestionOption[] = [
  { key: "yes", label: "Yes", score: 100 },
  { key: "partial", label: "Partial", score: 50 },
  { key: "no", label: "No", score: 0 },
];

const DEFAULT_SUB_FIELD_OPTIONS: QuestionOption[] = [
  { key: "low", label: "Low", score: 25 },
  { key: "medium", label: "Medium", score: 50 },
  { key: "high", label: "High", score: 75 },
  { key: "critical", label: "Critical", score: 100 },
];

const DEFAULT_SUB_FIELDS: QuestionSubField[] = [
  { key: "confidentiality", label: "Confidentiality", options: [...DEFAULT_SUB_FIELD_OPTIONS] },
  { key: "integrity", label: "Integrity", options: [...DEFAULT_SUB_FIELD_OPTIONS] },
  { key: "availability", label: "Availability", options: [...DEFAULT_SUB_FIELD_OPTIONS] },
];

/** Convert stored options to the API format the scoring engine expects. */
function buildOptionsPayload(
  type: QuestionType,
  opts: QuestionOption[],
): unknown {
  if (type === "WEIGHTED_YES_NO") {
    // Nested object format: { yes: { score, label }, partial: { score, label }, no: { score, label } }
    const map: Record<string, { score: number; label: string }> = {};
    for (const o of opts) {
      map[o.key] = { score: o.score, label: o.label };
    }
    return map;
  }
  if (type === "WEIGHTED_MULTIPLE_CHOICE") {
    // Array format: [{ key, label, score }, ...]
    return opts.map((o) => ({ key: o.key, label: o.label, score: o.score }));
  }
  return undefined;
}

/** Parse existing options from the backend into the internal QuestionOption[] format. */
function parseStoredOptions(
  type: QuestionType,
  raw: unknown,
): QuestionOption[] | null {
  if (!raw) return null;

  if (type === "WEIGHTED_YES_NO") {
    // Nested: { yes: { score, label }, ... }  OR  flat: { yes: 100, ... }
    if (typeof raw === "object" && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      const result: QuestionOption[] = [];
      for (const key of ["yes", "partial", "partially", "no"]) {
        const entry = obj[key];
        if (entry === undefined) continue;
        if (typeof entry === "number") {
          result.push({ key, label: key.charAt(0).toUpperCase() + key.slice(1), score: entry });
        } else if (typeof entry === "object" && entry !== null) {
          const e = entry as { score?: number; label?: string };
          result.push({ key, label: e.label ?? key, score: e.score ?? 0 });
        }
      }
      // Normalise "partially" → "partial" for display
      return result.length > 0
        ? result.map((o) => o.key === "partially" ? { ...o, key: "partial" } : o)
        : null;
    }
  }

  if (type === "WEIGHTED_MULTIPLE_CHOICE") {
    // Array: [{ key, label, score }]
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map((o: Record<string, unknown>) => ({
        key: String(o.key ?? o.value ?? ""),
        label: String(o.label ?? ""),
        score: Number(o.score ?? 0),
      }));
    }
    // Object with choices: { choices: [{ value, score, label }] }
    if (typeof raw === "object" && !Array.isArray(raw)) {
      const obj = raw as { choices?: unknown[] };
      if (Array.isArray(obj.choices) && obj.choices.length > 0) {
        return obj.choices.map((o) => {
          const entry = o as Record<string, unknown>;
          return {
            key: String(entry.key ?? entry.value ?? ""),
            label: String(entry.label ?? ""),
            score: Number(entry.score ?? 0),
          };
        });
      }
    }
  }

  return null;
}

/** Convert sub-fields into the storage shape: { subFields: [{ key, label, options:[{key,label,score}] }] } */
function buildSubFieldsPayload(subFields: QuestionSubField[]): unknown {
  return {
    subFields: subFields.map((sf) => ({
      key: sf.key,
      label: sf.label,
      options: sf.options.map((o) => ({ key: o.key, label: o.label, score: o.score })),
    })),
  };
}

/** Parse stored MULTI_SUB_FIELD options into the editor's internal shape. */
function parseStoredSubFields(raw: unknown): QuestionSubField[] | null {
  if (!raw || typeof raw !== "object") return null;
  const arr = (raw as { subFields?: unknown }).subFields;
  if (!Array.isArray(arr) || arr.length === 0) return null;

  const result: QuestionSubField[] = arr.map((sfRaw) => {
    const sf = sfRaw as Record<string, unknown>;
    const key = String(sf.key ?? sf.name ?? "");
    const label = String(sf.label ?? sf.name ?? key);
    const opts = Array.isArray(sf.options) ? sf.options : [];
    return {
      key,
      label,
      options: opts.map((oRaw) => {
        const o = oRaw as Record<string, unknown>;
        return {
          key: String(o.key ?? o.value ?? ""),
          label: String(o.label ?? ""),
          score: Number(o.score ?? 0),
        };
      }),
    };
  });

  return result.length > 0 ? result : null;
}

// ── SubFields editor (MULTI_SUB_FIELD) ────────

interface SubFieldsEditorProps {
  subFields: QuestionSubField[];
  onChange: (next: QuestionSubField[]) => void;
}

function SubFieldsEditor({ subFields, onChange }: SubFieldsEditorProps) {
  const { t } = useTranslation();

  function updateSubField(idx: number, patch: Partial<QuestionSubField>) {
    onChange(subFields.map((sf, i) => (i === idx ? { ...sf, ...patch } : sf)));
  }

  function removeSubField(idx: number) {
    onChange(subFields.filter((_, i) => i !== idx));
  }

  function addSubField() {
    onChange([
      ...subFields,
      { key: "", label: "", options: [...DEFAULT_SUB_FIELD_OPTIONS] },
    ]);
  }

  function updateOption(sfIdx: number, optIdx: number, patch: Partial<QuestionOption>) {
    const sf = subFields[sfIdx];
    if (!sf) return;
    const nextOpts = sf.options.map((o, i) =>
      i === optIdx
        ? {
            ...o,
            ...patch,
            score:
              patch.score !== undefined
                ? Math.max(0, Math.min(100, Number(patch.score) || 0))
                : o.score,
          }
        : o,
    );
    updateSubField(sfIdx, { options: nextOpts });
  }

  function addOption(sfIdx: number) {
    const sf = subFields[sfIdx];
    if (!sf) return;
    updateSubField(sfIdx, { options: [...sf.options, { key: "", label: "", score: 0 }] });
  }

  function removeOption(sfIdx: number, optIdx: number) {
    const sf = subFields[sfIdx];
    if (!sf) return;
    updateSubField(sfIdx, { options: sf.options.filter((_, i) => i !== optIdx) });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label className="text-[14px] font-medium text-text-secondary">
          {t("schema.edit.subFieldsLabel")}
        </label>
        <button
          type="button"
          onClick={addSubField}
          className="flex items-center gap-1 text-[12px] text-accent hover:text-accent-light"
        >
          <Plus className="h-3 w-3" />
          {t("schema.edit.addSubField")}
        </button>
      </div>

      {subFields.length === 0 && (
        <p className="rounded-control border border-dashed border-border-default py-3 text-center text-[12px] text-text-muted">
          {t("schema.edit.noSubFields")}
        </p>
      )}

      {subFields.map((sf, sfIdx) => (
        <div
          key={sfIdx}
          className="flex flex-col gap-2 rounded-control border border-border-default bg-card p-3"
        >
          <div className="flex items-center gap-2">
            <Input
              className="h-7 w-32 text-[13px] font-mono"
              placeholder={t("schema.edit.keyPlaceholder")}
              value={sf.key}
              onChange={(e) => updateSubField(sfIdx, { key: e.target.value })}
            />
            <Input
              className="h-7 flex-1 text-[13px]"
              placeholder={t("schema.edit.subFieldLabelPlaceholder")}
              value={sf.label}
              onChange={(e) => updateSubField(sfIdx, { label: e.target.value })}
            />
            <button
              type="button"
              onClick={() => removeSubField(sfIdx)}
              className="flex h-6 w-6 items-center justify-center text-text-muted hover:text-danger"
              title={t("schema.edit.removeSubField")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Per-subfield options */}
          <div className="ml-2 flex flex-col gap-1.5 border-l border-border-default pl-3">
            <div className="grid grid-cols-[120px_1fr_80px_24px] gap-2 px-1 text-[11px] font-medium text-text-muted">
              <span>{t("schema.edit.colKey")}</span>
              <span>{t("schema.edit.colLabel")}</span>
              <span>{t("schema.edit.colScore")}</span>
              <span />
            </div>
            {sf.options.map((opt, optIdx) => (
              <div
                key={optIdx}
                className="grid grid-cols-[120px_1fr_80px_24px] gap-2 items-center"
              >
                <Input
                  className="h-7 text-[13px] font-mono"
                  placeholder={t("schema.edit.optionKeyExample")}
                  value={opt.key}
                  onChange={(e) => updateOption(sfIdx, optIdx, { key: e.target.value })}
                />
                <Input
                  className="h-7 text-[13px]"
                  placeholder={t("schema.edit.colLabel")}
                  value={opt.label}
                  onChange={(e) => updateOption(sfIdx, optIdx, { label: e.target.value })}
                />
                <Input
                  className="h-7 text-[13px] text-right"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={opt.score}
                  onChange={(e) => updateOption(sfIdx, optIdx, { score: Number(e.target.value) })}
                />
                <button
                  type="button"
                  onClick={() => removeOption(sfIdx, optIdx)}
                  className="flex h-6 w-6 items-center justify-center text-text-muted hover:text-danger"
                  title={t("schema.edit.removeOption")}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addOption(sfIdx)}
              className="flex items-center gap-1 self-start px-1 text-[12px] text-accent hover:text-accent-light"
            >
              <Plus className="h-3 w-3" />
              {t("schema.edit.addOption")}
            </button>
          </div>
        </div>
      ))}

      <p className="text-[11px] text-text-muted px-1">
        {t("schema.edit.subFieldsHelp")}
      </p>
    </div>
  );
}

// ── Shared OptionsEditor component ────────────

interface OptionsEditorProps {
  type: "WEIGHTED_MULTIPLE_CHOICE" | "WEIGHTED_YES_NO";
  options: QuestionOption[];
  onChange: (opts: QuestionOption[]) => void;
}

function OptionsEditor({ type, options, onChange }: OptionsEditorProps) {
  const { t } = useTranslation();
  const isYesNo = type === "WEIGHTED_YES_NO";

  function updateScore(idx: number, value: string) {
    onChange(options.map((o, i) => (i === idx ? { ...o, score: Math.max(0, Math.min(100, Number(value) || 0)) } : o)));
  }

  function updateKey(idx: number, value: string) {
    onChange(options.map((o, i) => (i === idx ? { ...o, key: value } : o)));
  }

  function updateLabel(idx: number, value: string) {
    onChange(options.map((o, i) => (i === idx ? { ...o, label: value } : o)));
  }

  function addChoice() {
    onChange([...options, { key: "", label: "", score: 0 }]);
  }

  function removeChoice(idx: number) {
    onChange(options.filter((_, i) => i !== idx));
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-[14px] font-medium text-text-secondary">
          {isYesNo ? t("schema.edit.answerScores") : t("schema.edit.choices")}
        </label>
        {!isYesNo && (
          <button
            type="button"
            onClick={addChoice}
            className="flex items-center gap-1 text-[12px] text-accent hover:text-accent-light"
          >
            <Plus className="h-3 w-3" />
            {t("schema.edit.addChoice")}
          </button>
        )}
      </div>

      {/* Column headers */}
      <div className={`grid gap-2 px-1 text-[11px] font-medium text-text-muted ${isYesNo ? "grid-cols-[1fr_80px]" : "grid-cols-[120px_1fr_80px_24px]"}`}>
        {!isYesNo && <span>{t("schema.edit.colKey")}</span>}
        <span>{t("schema.edit.colLabel")}</span>
        <span>{t("schema.edit.colScore")}</span>
        {!isYesNo && <span />}
      </div>

      {options.map((opt, idx) => (
        <div
          key={idx}
          className={`grid gap-2 items-center rounded-control border border-border-default bg-card px-2 py-1.5 ${isYesNo ? "grid-cols-[1fr_80px]" : "grid-cols-[120px_1fr_80px_24px]"}`}
        >
          {!isYesNo && (
            <Input
              className="h-7 text-[13px] font-mono"
              placeholder={t("schema.edit.choiceKeyExample")}
              value={opt.key}
              onChange={(e) => updateKey(idx, e.target.value)}
            />
          )}
          <Input
            className="h-7 text-[13px]"
            placeholder={isYesNo ? opt.key : t("schema.edit.colLabel")}
            value={opt.label}
            onChange={(e) => updateLabel(idx, e.target.value)}
            readOnly={isYesNo && opt.key !== "partial"}
          />
          <Input
            className="h-7 text-[13px] text-right"
            type="number"
            min="0"
            max="100"
            step="1"
            value={opt.score}
            onChange={(e) => updateScore(idx, e.target.value)}
          />
          {!isYesNo && (
            <button
              type="button"
              onClick={() => removeChoice(idx)}
              className="flex h-6 w-6 items-center justify-center text-text-muted hover:text-danger"
              title={t("schema.edit.remove")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}

      {!isYesNo && options.length === 0 && (
        <p className="text-[12px] text-text-muted px-1">{t("schema.edit.noChoices")}</p>
      )}

      <p className="text-[11px] text-text-muted px-1">
        {isYesNo
          ? t("schema.edit.yesNoHelp")
          : t("schema.edit.choicesHelp")}
      </p>
    </div>
  );
}

// ── Add Domain Dialog ─────────────────────────

interface AddDomainDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  schemaId: string;
  vId: string;
  nextSortOrder: number;
  onAdded: () => void;
}

function AddDomainDialog({ open, onOpenChange, schemaId, vId, nextSortOrder, onAdded }: AddDomainDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ code?: string; name?: string }>({});

  function validate() {
    const next: typeof errors = {};
    if (!code.trim()) next.code = t("schema.edit.codeRequired");
    if (!name.trim()) next.name = t("schema.createDialog.nameRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await api.post(`/schemas/${schemaId}/versions/${vId}/domains`, {
        code: code.toUpperCase().trim(),
        name: name.trim(),
        sortOrder: nextSortOrder,
      });
      toast.success(t("schema.edit.domainAddedTitle"), t("schema.edit.domainAddedDesc", { name }));
      setCode("");
      setName("");
      setErrors({});
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error(t("schema.edit.addDomainFailed"), err instanceof Error ? err.message : t("common.unknownError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    if (isSubmitting) return;
    setCode("");
    setName("");
    setErrors({});
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("schema.edit.addDomain")}</DialogTitle>
          <DialogDescription>
            {t("schema.edit.addDomainDesc")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <InputGroup
            label={t("schema.questionTable.code")}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={t("schema.edit.domainCodePlaceholder")}
            errorText={errors.code}
          />
          <InputGroup
            label={t("schema.name")}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("schema.edit.domainNamePlaceholder")}
            errorText={errors.name}
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {t("schema.edit.addDomain")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Add Posture Dialog ────────────────────────

interface AddPostureDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  schemaId: string;
  vId: string;
  domains: Domain[];
  nextSortOrder: number;
  onAdded: () => void;
}

function AddPostureDialog({ open, onOpenChange, schemaId, vId, domains, nextSortOrder, onAdded }: AddPostureDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [weight, setWeight] = useState("1");
  const [domainId, setDomainId] = useState(domains[0]?.id ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ code?: string; name?: string; domainId?: string }>({});

  useEffect(() => {
    if (domains.length > 0 && !domainId) setDomainId(domains[0]?.id ?? "");
  }, [domains, domainId]);

  function validate() {
    const next: typeof errors = {};
    if (!code.trim()) next.code = t("schema.edit.codeRequired");
    if (!name.trim()) next.name = t("schema.createDialog.nameRequired");
    if (!domainId) next.domainId = t("schema.edit.domainRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await api.post(`/schemas/${schemaId}/versions/${vId}/postures`, {
        code: code.toUpperCase().trim(),
        name: name.trim(),
        weight: parseFloat(weight) || 1,
        sortOrder: nextSortOrder,
        domainId,
      });
      toast.success(t("schema.edit.postureAddedTitle"), t("schema.edit.postureAddedDesc", { name }));
      setCode("");
      setName("");
      setWeight("1");
      setErrors({});
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error(t("schema.edit.addPostureFailed"), err instanceof Error ? err.message : t("common.unknownError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    if (isSubmitting) return;
    setCode("");
    setName("");
    setWeight("1");
    setErrors({});
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("schema.edit.addPosture")}</DialogTitle>
          <DialogDescription>
            {t("schema.edit.addPostureDesc")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("schema.edit.domainLabel")} <span className="text-danger">*</span>
            </label>
            <SelectRoot value={domainId} onValueChange={setDomainId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    domains.length === 0
                      ? t("schema.edit.addDomainFirst")
                      : t("schema.edit.selectDomain")
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {domains.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.code} - {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
            {errors.domainId && (
              <p className="text-[12px] text-danger">{errors.domainId}</p>
            )}
            <p className="text-[12px] text-text-muted">
              {t("schema.edit.postureHint")}
            </p>
          </div>
          <InputGroup
            label={t("schema.questionTable.code")}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={t("schema.edit.postureCodePlaceholder")}
            errorText={errors.code}
          />
          <InputGroup
            label={t("schema.name")}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("schema.edit.postureNamePlaceholder")}
            errorText={errors.name}
          />
          <InputGroup
            label={t("schema.questionTable.weight")}
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting} disabled={domains.length === 0}>
              {t("schema.edit.addPosture")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Posture Dialog ───────────────────────

interface EditPostureDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  schemaId: string;
  vId: string;
  posture: Posture;
  onSaved: () => void;
}

function EditPostureDialog({ open, onOpenChange, schemaId, vId, posture, onSaved }: EditPostureDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState(posture.code);
  const [name, setName] = useState(posture.name);
  const [weight, setWeight] = useState(String(posture.weight));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ code?: string; name?: string }>({});

  function validate() {
    const next: typeof errors = {};
    if (!code.trim()) next.code = t("schema.edit.codeRequired");
    if (!name.trim()) next.name = t("schema.createDialog.nameRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await api.put(`/schemas/${schemaId}/versions/${vId}/postures/${posture.id}`, {
        code: code.toUpperCase().trim(),
        name: name.trim(),
        weight: parseFloat(weight) || 1,
      });
      toast.success(t("schema.edit.postureUpdatedTitle"), t("schema.edit.postureSavedDesc", { name }));
      onSaved();
    } catch (err) {
      toast.error(t("schema.edit.updatePostureFailed"), err instanceof Error ? err.message : t("common.unknownError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isSubmitting) onOpenChange(v); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("schema.edit.editPosture")}</DialogTitle>
          <DialogDescription>{t("schema.edit.editPostureDesc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <InputGroup
            label={t("schema.questionTable.code")}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            errorText={errors.code}
          />
          <InputGroup
            label={t("schema.name")}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            errorText={errors.name}
          />
          <InputGroup
            label={t("schema.questionTable.weight")}
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Add Question Dialog ───────────────────────

interface AddQuestionDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  schemaId: string;
  vId: string;
  postureId: string;
  /** The posture's domain — when set, the question's domain is locked to it (backend enforces the match). */
  postureDomainId?: string | null;
  domains: Domain[];
  nextSortOrder: number;
  onAdded: () => void;
}

function AddQuestionDialog({
  open,
  onOpenChange,
  schemaId,
  vId,
  postureId,
  postureDomainId,
  domains,
  nextSortOrder,
  onAdded,
}: AddQuestionDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [text, setText] = useState("");
  const [type, setType] = useState<QuestionType>("WEIGHTED_YES_NO");
  const [domainId, setDomainId] = useState(postureDomainId ?? domains[0]?.id ?? "");
  const [weight, setWeight] = useState("1");
  const [options, setOptions] = useState<QuestionOption[]>(DEFAULT_YES_NO_OPTIONS);
  const [subFields, setSubFields] = useState<QuestionSubField[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ code?: string; text?: string; domainId?: string }>({});

  useEffect(() => {
    if (domains.length > 0 && !domainId) setDomainId(domains[0]?.id ?? "");
  }, [domains, domainId]);

  // Reset options/sub-fields to sensible defaults when the type changes.
  function handleTypeChange(v: QuestionType) {
    setType(v);
    if (v === "WEIGHTED_YES_NO") {
      setOptions([...DEFAULT_YES_NO_OPTIONS]);
      setSubFields([]);
    } else if (v === "WEIGHTED_MULTIPLE_CHOICE") {
      setOptions([...DEFAULT_MC_OPTIONS]);
      setSubFields([]);
    } else if (v === "MULTI_SUB_FIELD") {
      setOptions([]);
      setSubFields(DEFAULT_SUB_FIELDS.map((sf) => ({ ...sf, options: [...sf.options] })));
    } else {
      setOptions([]);
      setSubFields([]);
    }
  }

  function validate() {
    const next: typeof errors = {};
    if (!code.trim()) next.code = t("schema.edit.codeRequired");
    if (!text.trim()) next.text = t("schema.edit.questionTextRequired");
    if (!domainId) next.domainId = t("schema.edit.domainRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function resetForm() {
    setCode("");
    setText("");
    setType("WEIGHTED_YES_NO");
    setOptions([...DEFAULT_YES_NO_OPTIONS]);
    setSubFields([]);
    setWeight("1");
    setErrors({});
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    const hasOptions = type === "WEIGHTED_YES_NO" || type === "WEIGHTED_MULTIPLE_CHOICE";
    const hasSubFields = type === "MULTI_SUB_FIELD";
    try {
      await api.post(`/schemas/${schemaId}/versions/${vId}/postures/${postureId}/questions`, {
        code: code.trim(),
        text: text.trim(),
        type,
        domainId,
        weight: parseFloat(weight) || 1,
        sortOrder: nextSortOrder,
        required: true,
        options: hasOptions
          ? buildOptionsPayload(type, options)
          : hasSubFields
            ? buildSubFieldsPayload(subFields)
            : undefined,
      });
      toast.success(t("schema.edit.questionAddedTitle"), t("schema.edit.questionAddedDesc", { code }));
      resetForm();
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error(t("schema.edit.addQuestionFailed"), err instanceof Error ? err.message : t("common.unknownError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    if (isSubmitting) return;
    resetForm();
    onOpenChange(false);
  }

  const showOptions = type === "WEIGHTED_MULTIPLE_CHOICE" || type === "WEIGHTED_YES_NO";
  const showSubFields = type === "MULTI_SUB_FIELD";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("schema.edit.addQuestion")}</DialogTitle>
          <DialogDescription>
            {t("schema.edit.addQuestionDesc")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <InputGroup
            label={t("schema.questionTable.code")}
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t("schema.edit.questionCodePlaceholder")}
            errorText={errors.code}
          />

          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("schema.edit.domainLabel")} <span className="text-danger">*</span>
            </label>
            <SelectRoot
              value={domainId}
              onValueChange={setDomainId}
              disabled={Boolean(postureDomainId)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("schema.edit.selectDomain")} />
              </SelectTrigger>
              <SelectContent>
                {domains.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.code} - {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
            {postureDomainId && (
              <p className="text-[12px] text-text-muted">{t("schema.edit.domainLockedToPosture")}</p>
            )}
            {errors.domainId && (
              <p className="text-[12px] text-danger">{errors.domainId}</p>
            )}
          </div>

          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("schema.type")}
            </label>
            <SelectRoot value={type} onValueChange={(v) => handleTypeChange(v as QuestionType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUESTION_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>

          <TextareaGroup
            label={t("schema.edit.questionText")}
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("schema.edit.questionTextPlaceholder")}
            rows={3}
            errorText={errors.text}
          />

          <InputGroup
            label={t("schema.questionTable.weight")}
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />

          {showOptions && (
            <OptionsEditor
              type={type as "WEIGHTED_MULTIPLE_CHOICE" | "WEIGHTED_YES_NO"}
              options={options}
              onChange={setOptions}
            />
          )}

          {showSubFields && (
            <SubFieldsEditor subFields={subFields} onChange={setSubFields} />
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {t("schema.edit.addQuestion")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Question Dialog ──────────────────────

interface EditQuestionDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  schemaId: string;
  vId: string;
  question: Question | null;
  /** The question's posture's domain — when set, the domain select is locked (backend enforces the match). */
  postureDomainId?: string | null;
  domains: Domain[];
  onSaved: () => void;
}

function EditQuestionDialog({
  open,
  onOpenChange,
  schemaId,
  vId,
  question,
  postureDomainId,
  domains,
  onSaved,
}: EditQuestionDialogProps) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [type, setType] = useState<QuestionType>("WEIGHTED_YES_NO");
  const [domainId, setDomainId] = useState("");
  const [weight, setWeight] = useState("1");
  const [required, setRequired] = useState(true);
  const [options, setOptions] = useState<QuestionOption[]>([...DEFAULT_YES_NO_OPTIONS]);
  const [subFields, setSubFields] = useState<QuestionSubField[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate state from the question prop whenever it changes.
  useEffect(() => {
    if (!question) return;
    setText(question.text);
    setType(question.type);
    setDomainId(question.domainId);
    setWeight(String(question.weight ?? 1));
    setRequired(question.required ?? true);

    if (question.type === "MULTI_SUB_FIELD") {
      const parsedSF = parseStoredSubFields(question.options as unknown);
      setSubFields(
        parsedSF ?? DEFAULT_SUB_FIELDS.map((sf) => ({ ...sf, options: [...sf.options] })),
      );
      setOptions([]);
    } else {
      setSubFields([]);
      const parsed = parseStoredOptions(question.type, question.options as unknown);
      if (parsed) {
        setOptions(parsed);
      } else if (question.type === "WEIGHTED_YES_NO") {
        setOptions([...DEFAULT_YES_NO_OPTIONS]);
      } else if (question.type === "WEIGHTED_MULTIPLE_CHOICE") {
        setOptions([...DEFAULT_MC_OPTIONS]);
      } else {
        setOptions([]);
      }
    }
  }, [question]);

  // When type changes in the editor, reset to sensible defaults.
  function handleTypeChange(v: QuestionType) {
    setType(v);
    if (v === "WEIGHTED_YES_NO") {
      setOptions([...DEFAULT_YES_NO_OPTIONS]);
      setSubFields([]);
    } else if (v === "WEIGHTED_MULTIPLE_CHOICE") {
      setOptions([...DEFAULT_MC_OPTIONS]);
      setSubFields([]);
    } else if (v === "MULTI_SUB_FIELD") {
      setOptions([]);
      setSubFields(DEFAULT_SUB_FIELDS.map((sf) => ({ ...sf, options: [...sf.options] })));
    } else {
      setOptions([]);
      setSubFields([]);
    }
  }

  const showOptions = type === "WEIGHTED_MULTIPLE_CHOICE" || type === "WEIGHTED_YES_NO";
  const showSubFields = type === "MULTI_SUB_FIELD";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!question) return;
    setIsSubmitting(true);
    try {
      await api.put(`/schemas/${schemaId}/versions/${vId}/questions/${question.id}`, {
        text: text.trim(),
        type,
        domainId,
        weight: parseFloat(weight) || 1,
        required,
        options: showOptions
          ? buildOptionsPayload(type, options)
          : showSubFields
            ? buildSubFieldsPayload(subFields)
            : undefined,
      });
      toast.success(t("schema.edit.questionSavedTitle"), t("schema.edit.changesSavedDesc"));
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(t("schema.edit.saveFailedTitle"), err instanceof Error ? err.message : t("common.unknownError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    if (isSubmitting) return;
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("schema.edit.editQuestion")}</DialogTitle>
          <DialogDescription>
            {question?.code}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">{t("schema.edit.domainLabel")}</label>
            <SelectRoot
              value={domainId}
              onValueChange={setDomainId}
              disabled={Boolean(postureDomainId)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("schema.edit.selectDomain")} />
              </SelectTrigger>
              <SelectContent>
                {domains.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.code} - {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
            {postureDomainId && (
              <p className="text-[12px] text-text-muted">{t("schema.edit.domainLockedToPosture")}</p>
            )}
          </div>

          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">{t("schema.type")}</label>
            <SelectRoot value={type} onValueChange={(v) => handleTypeChange(v as QuestionType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUESTION_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>

          <TextareaGroup
            label={t("schema.edit.questionText")}
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
          />

          <div className="flex gap-4">
            <div className="flex-1">
              <InputGroup
                label={t("schema.questionTable.weight")}
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2 pb-[2px]">
              <label className="flex items-center gap-2 cursor-pointer text-[14px] text-text-secondary">
                <input
                  type="checkbox"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  className="h-4 w-4 rounded border-border-default"
                />
                {t("common.required")}
              </label>
            </div>
          </div>

          {showOptions && (
            <OptionsEditor
              type={type as "WEIGHTED_MULTIPLE_CHOICE" | "WEIGHTED_YES_NO"}
              options={options}
              onChange={setOptions}
            />
          )}

          {showSubFields && (
            <SubFieldsEditor subFields={subFields} onChange={setSubFields} />
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting} leadingIcon={<Save className="h-4 w-4" />}>
              {t("schema.edit.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Question row ──────────────────────────────

function QuestionRow({
  question,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  question: Question;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-2 rounded-control border border-border-default bg-card p-3 text-[13px]">
      <GripVertical className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-muted" />
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] text-text-muted">{question.code}</span>
          <span className="inline-flex items-center rounded-badge bg-hover px-1.5 py-0.5 text-[10px] font-semibold text-text-muted">
            {t(`schema.questionTypes.${question.type}`)}
          </span>
          <span className="text-[11px] text-text-muted">{t("schema.edit.weightAbbrev")}{question.weight}</span>
        </div>
        <p className="line-clamp-2 text-text-secondary">{question.text}</p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1">
        <button
          type="button"
          disabled={isFirst}
          onClick={onMoveUp}
          className="flex h-6 w-6 items-center justify-center rounded text-text-muted disabled:opacity-30 hover:bg-hover hover:text-text-secondary"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={isLast}
          onClick={onMoveDown}
          className="flex h-6 w-6 items-center justify-center rounded text-text-muted disabled:opacity-30 hover:bg-hover hover:text-text-secondary"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:bg-hover hover:text-text-secondary"
          title={t("schema.edit.editQuestionTitle")}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:bg-danger-bg hover:text-danger"
          title={t("schema.edit.deleteQuestionTitle")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Main editor ───────────────────────────────

export function SchemaEdit() {
  const { t } = useTranslation();
  const { id, vId } = useParams<{ id: string; vId: string }>();
  const navigate = useNavigate();

  const [schema, setSchema] = useState<SchemaRecord | null>(null);
  const [version, setVersion] = useState<SchemaVersionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Metadata edit state
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isSavingMeta, setIsSavingMeta] = useState(false);

  // Domain/Question/Posture dialogs
  const [addDomainOpen, setAddDomainOpen] = useState(false);
  const [addPostureOpen, setAddPostureOpen] = useState(false);
  const [addQuestionPostureId, setAddQuestionPostureId] = useState<string | null>(null);
  const [editQuestion, setEditQuestion] = useState<Question | null>(null);
  const [editPosture, setEditPosture] = useState<Posture | null>(null);

  // Load data
  async function loadVersion() {
    if (!id || !vId) return;
    try {
      const [schemaRes, versionRes] = await Promise.all([
        api.get<{ data: SchemaRecord }>(`/schemas/${id}`),
        api.get<{ data: SchemaVersionDetail }>(`/schemas/${id}/versions/${vId}`),
      ]);
      const s = schemaRes.data;
      const v = versionRes.data;
      setSchema(s);
      setVersion(v);
      setEditName(s.name ?? "");
      setEditDescription(s.description ?? "");

      // Guard: only editable if DRAFT and not preset
      if (v.status !== "DRAFT" || s.isPreset) {
        toast.error(t("schema.edit.notEditableTitle"), t("schema.edit.notEditableDesc"));
        navigate(`/schemas/${id}/versions/${vId}`);
      }
    } catch {
      toast.error(t("schema.edit.loadFailedTitle"), t("schema.edit.loadFailedDesc"));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, vId]);

  async function handleSaveMeta(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setIsSavingMeta(true);
    try {
      await api.put(`/schemas/${id}`, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      toast.success(t("schema.edit.savedTitle"), t("schema.edit.metadataUpdatedDesc"));
      await loadVersion();
    } catch (err) {
      toast.error(t("schema.edit.saveFailedTitle"), err instanceof Error ? err.message : t("common.unknownError"));
    } finally {
      setIsSavingMeta(false);
    }
  }

  async function handleDeleteDomain(dId: string) {
    if (!id || !vId) return;
    try {
      await api.delete(`/schemas/${id}/versions/${vId}/domains/${dId}`);
      toast.success(t("schema.edit.domainRemovedTitle"), "");
      await loadVersion();
    } catch (err) {
      toast.error(t("schema.edit.cannotRemoveDomain"), err instanceof Error ? err.message : t("common.unknownError"));
    }
  }

  async function handleDeleteQuestion(qId: string) {
    if (!id || !vId) return;
    try {
      await api.delete(`/schemas/${id}/versions/${vId}/questions/${qId}`);
      toast.success(t("schema.edit.questionRemovedTitle"), "");
      await loadVersion();
    } catch (err) {
      toast.error(t("schema.edit.cannotRemoveQuestion"), err instanceof Error ? err.message : t("common.unknownError"));
    }
  }

  async function handleMoveQuestion(question: Question, direction: "up" | "down", siblings: Question[]) {
    if (!id || !vId) return;
    const idx = siblings.findIndex((q) => q.id === question.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;

    const swapQ = siblings[swapIdx];
    if (!swapQ) return;
    try {
      await Promise.all([
        api.put(`/schemas/${id}/versions/${vId}/questions/${question.id}`, {
          sortOrder: swapQ.sortOrder,
        }),
        api.put(`/schemas/${id}/versions/${vId}/questions/${swapQ.id}`, {
          sortOrder: question.sortOrder,
        }),
      ]);
      await loadVersion();
    } catch (err) {
      toast.error(t("schema.edit.reorderFailed"), err instanceof Error ? err.message : t("common.unknownError"));
    }
  }

  async function handleMoveDomain(domain: Domain, direction: "up" | "down", allDomains: Domain[]) {
    if (!id || !vId) return;
    const idx = allDomains.findIndex((d) => d.id === domain.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= allDomains.length) return;

    const swapD = allDomains[swapIdx];
    if (!swapD) return;
    try {
      await Promise.all([
        api.put(`/schemas/${id}/versions/${vId}/domains/${domain.id}`, {
          sortOrder: swapD.sortOrder,
        }),
        api.put(`/schemas/${id}/versions/${vId}/domains/${swapD.id}`, {
          sortOrder: domain.sortOrder,
        }),
      ]);
      await loadVersion();
    } catch (err) {
      toast.error(t("schema.edit.reorderFailed"), err instanceof Error ? err.message : t("common.unknownError"));
    }
  }

  // S-109: postures previously had only an Add path + a read-only chip list.
  // Delete/reorder mirror the domain handlers; the backend blocks deleting a
  // posture that still has questions (DRAFT-only), surfaced here as a toast.
  async function handleDeletePosture(pId: string) {
    if (!id || !vId) return;
    try {
      await api.delete(`/schemas/${id}/versions/${vId}/postures/${pId}`);
      toast.success(t("schema.edit.postureRemovedTitle"), "");
      await loadVersion();
    } catch (err) {
      toast.error(t("schema.edit.cannotRemovePosture"), err instanceof Error ? err.message : t("common.unknownError"));
    }
  }

  async function handleMovePosture(posture: Posture, direction: "up" | "down", siblings: Posture[]) {
    if (!id || !vId) return;
    const idx = siblings.findIndex((p) => p.id === posture.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;

    const swapP = siblings[swapIdx];
    if (!swapP) return;
    try {
      await Promise.all([
        api.put(`/schemas/${id}/versions/${vId}/postures/${posture.id}`, {
          sortOrder: swapP.sortOrder,
        }),
        api.put(`/schemas/${id}/versions/${vId}/postures/${swapP.id}`, {
          sortOrder: posture.sortOrder,
        }),
      ]);
      await loadVersion();
    } catch (err) {
      toast.error(t("schema.edit.reorderFailed"), err instanceof Error ? err.message : t("common.unknownError"));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32 text-text-muted">
        {t("common.loading")}
      </div>
    );
  }

  if (!version || !schema) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-32">
        <p className="text-body text-text-muted">{t("schema.detail.notFound")}</p>
        <Button variant="outline" onClick={() => navigate("/schemas")}>
          {t("schema.detail.backToSchemas")}
        </Button>
      </div>
    );
  }

  const domains = [...(version.domains ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const postures = [...(version.postures ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  // For each domain, gather all questions that belong to it (across all postures)
  const questionsByDomain = new Map<string, Question[]>();
  for (const posture of postures) {
    for (const q of posture.questions) {
      const arr = questionsByDomain.get(q.domainId) ?? [];
      arr.push(q);
      questionsByDomain.set(q.domainId, arr);
    }
  }

  const nextDomainSortOrder =
    domains.length > 0 ? Math.max(...domains.map((d) => d.sortOrder)) + 1 : 0;

  return (
    <>
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-[24px] font-semibold text-text-primary">
            {t("schema.edit.pageTitle", { name: schema.name })}
          </h1>
          <p className="text-body text-text-secondary">
            {t("schema.edit.versionDraft", { version: version.version })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/schemas/${id}/versions/${vId}`)}
          >
            {t("schema.edit.backToView")}
          </Button>
        </div>
      </div>

      {/* ── Metadata section ── */}
      <div className="rounded-card border border-border-default bg-card p-5">
        <h2 className="mb-4 text-[16px] font-medium text-text-primary">
          {t("schema.edit.metadataHeading")}
        </h2>
        <form onSubmit={handleSaveMeta} className="flex flex-col gap-4">
          <InputGroup
            label={t("schema.name")}
            required
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder={t("schema.edit.namePlaceholder")}
          />
          <TextareaGroup
            label={t("schema.edit.description")}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder={t("schema.edit.descriptionPlaceholder")}
            rows={3}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              isLoading={isSavingMeta}
              leadingIcon={<Save className="h-4 w-4" />}
            >
              {t("schema.edit.saveMetadata")}
            </Button>
          </div>
        </form>
      </div>

      {/* ── Domains section ── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-medium text-text-primary">
            {t("schema.domains")}
            <span className="ml-2 text-[14px] text-text-muted">({domains.length})</span>
          </h2>
          <Button
            variant="outline"
            size="sm"
            leadingIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setAddDomainOpen(true)}
          >
            {t("schema.edit.addDomain")}
          </Button>
        </div>

        {domains.length === 0 ? (
          <div className="rounded-card border border-border-default bg-card py-12 text-center text-body text-text-muted">
            {t("schema.edit.noDomainsYet")}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {domains.map((domain, dIdx) => {
              const domainQuestions = (questionsByDomain.get(domain.id) ?? []).sort(
                (a, b) => a.sortOrder - b.sortOrder,
              );

              // Only this domain's postures (a question's domain is forced to its
              // posture's domain, so cross-listing other domains' postures here
              // just showed misleading empty rows). Legacy postures without a
              // domain stay visible under every domain.
              const domainPostures = postures.filter(
                (p) => !p.domainId || p.domainId === domain.id,
              );

              return (
                <div
                  key={domain.id}
                  className="rounded-card border border-border-default bg-card"
                >
                  {/* Domain header */}
                  <div className="flex items-center gap-3 border-b border-border-default p-4">
                    <GripVertical className="h-4 w-4 text-text-muted" />
                    <span className="font-mono text-[12px] text-text-muted">{domain.code}</span>
                    <span className="flex-1 font-medium text-text-primary">{domain.name}</span>
                    <span className="text-[12px] text-text-muted">{t("schema.edit.questionCount", { count: domainQuestions.length })}</span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={dIdx === 0}
                        onClick={() => handleMoveDomain(domain, "up", domains)}
                        className="flex h-7 w-7 items-center justify-center rounded text-text-muted disabled:opacity-30 hover:bg-hover"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={dIdx === domains.length - 1}
                        onClick={() => handleMoveDomain(domain, "down", domains)}
                        className="flex h-7 w-7 items-center justify-center rounded text-text-muted disabled:opacity-30 hover:bg-hover"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={domainQuestions.length > 0}
                        onClick={() => handleDeleteDomain(domain.id)}
                        title={domainQuestions.length > 0 ? t("schema.edit.removeQuestionsFirst") : t("schema.edit.deleteDomainTitle")}
                        className="flex h-7 w-7 items-center justify-center rounded text-text-muted disabled:opacity-30 hover:bg-danger-bg hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Questions per posture */}
                  <div className="p-4">
                    {domainPostures.map((posture) => {
                      const postureQs = domainQuestions
                        .filter((q) => q.postureId === posture.id)
                        .sort((a, b) => a.sortOrder - b.sortOrder);

                      const nextQSortOrder =
                        postureQs.length > 0
                          ? Math.max(...postureQs.map((q) => q.sortOrder)) + 1
                          : 0;

                      return (
                        <div key={posture.id} className="mb-4 last:mb-0">
                          <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[11px] text-text-muted">
                                {posture.code}
                              </span>
                              <span className="text-[13px] font-medium text-text-secondary">
                                {posture.name}
                              </span>
                              <span className="text-[11px] text-text-muted">
                                {t("schema.edit.questionCount", { count: postureQs.length })}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setAddQuestionPostureId(posture.id)}
                              className="flex items-center gap-1 text-[12px] text-accent hover:text-accent-light"
                            >
                              <Plus className="h-3 w-3" />
                              {t("schema.edit.addQuestion")}
                            </button>
                          </div>

                          {postureQs.length === 0 ? (
                            <p className="rounded-control border border-dashed border-border-default py-4 text-center text-[12px] text-text-muted">
                              {t("schema.edit.noQuestionsInPostureDomain")}
                            </p>
                          ) : (
                            <div className="flex flex-col gap-1.5">
                              {postureQs.map((q, qIdx) => (
                                <QuestionRow
                                  key={q.id}
                                  question={q}
                                  isFirst={qIdx === 0}
                                  isLast={qIdx === postureQs.length - 1}
                                  onEdit={() => setEditQuestion(q)}
                                  onDelete={() => handleDeleteQuestion(q.id)}
                                  onMoveUp={() => handleMoveQuestion(q, "up", postureQs)}
                                  onMoveDown={() => handleMoveQuestion(q, "down", postureQs)}
                                />
                              ))}
                            </div>
                          )}

                          {/* Add question dialog trigger for this posture */}
                          {addQuestionPostureId === posture.id && (
                            <AddQuestionDialog
                              open={addQuestionPostureId === posture.id}
                              onOpenChange={(v) => { if (!v) setAddQuestionPostureId(null); }}
                              schemaId={id!}
                              vId={vId!}
                              postureId={posture.id}
                              postureDomainId={posture.domainId ?? null}
                              domains={domains}
                              nextSortOrder={nextQSortOrder}
                              onAdded={async () => {
                                setAddQuestionPostureId(null);
                                await loadVersion();
                              }}
                            />
                          )}
                        </div>
                      );
                    })}

                    {domainPostures.length === 0 && (
                      <p className="text-[12px] text-text-muted">
                        {t("schema.edit.noPosturesInline")}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Postures section ── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-medium text-text-primary">
            {t("schema.postures")}
            <span className="ml-2 text-[14px] text-text-muted">({postures.length})</span>
          </h2>
          <Button
            variant="outline"
            size="sm"
            leadingIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setAddPostureOpen(true)}
          >
            {t("schema.edit.addPosture")}
          </Button>
        </div>

        {postures.length === 0 ? (
          <div className="rounded-card border border-border-default bg-card py-12 text-center text-body text-text-muted">
            {t("schema.edit.noPosturesYet")}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {postures.map((p, pIdx) => (
              <div
                key={p.id}
                className="flex items-center gap-3 rounded-control border border-border-default bg-card px-3 py-2 text-[13px]"
              >
                <span className="font-mono text-[11px] text-text-muted">{p.code}</span>
                <span className="flex-1 text-text-secondary">{p.name}</span>
                <span className="text-[11px] text-text-muted">{t("schema.edit.questionCount", { count: p.questions.length })}</span>
                <span className="text-[11px] text-text-muted">{t("schema.edit.weightAbbrev")}{p.weight}</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={pIdx === 0}
                    onClick={() => handleMovePosture(p, "up", postures)}
                    title={t("schema.edit.moveUp")}
                    className="flex h-7 w-7 items-center justify-center rounded text-text-muted disabled:opacity-30 hover:bg-hover"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={pIdx === postures.length - 1}
                    onClick={() => handleMovePosture(p, "down", postures)}
                    title={t("schema.edit.moveDown")}
                    className="flex h-7 w-7 items-center justify-center rounded text-text-muted disabled:opacity-30 hover:bg-hover"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditPosture(p)}
                    title={t("schema.edit.editPostureTitle")}
                    className="flex h-7 w-7 items-center justify-center rounded text-text-muted hover:bg-hover hover:text-text-primary"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={p.questions.length > 0}
                    onClick={() => handleDeletePosture(p.id)}
                    title={p.questions.length > 0 ? t("schema.edit.removeQuestionsFirst") : t("schema.edit.deletePostureTitle")}
                    className="flex h-7 w-7 items-center justify-center rounded text-text-muted disabled:opacity-30 hover:bg-danger-bg hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddDomainDialog
        open={addDomainOpen}
        onOpenChange={setAddDomainOpen}
        schemaId={id!}
        vId={vId!}
        nextSortOrder={nextDomainSortOrder}
        onAdded={async () => {
          setAddDomainOpen(false);
          await loadVersion();
        }}
      />

      <AddPostureDialog
        open={addPostureOpen}
        onOpenChange={setAddPostureOpen}
        schemaId={id!}
        vId={vId!}
        domains={domains}
        nextSortOrder={postures.length > 0 ? Math.max(...postures.map((p) => p.sortOrder)) + 1 : 0}
        onAdded={async () => {
          setAddPostureOpen(false);
          await loadVersion();
        }}
      />

      {editPosture && (
        <EditPostureDialog
          open={Boolean(editPosture)}
          onOpenChange={(v) => { if (!v) setEditPosture(null); }}
          schemaId={id!}
          vId={vId!}
          posture={editPosture}
          onSaved={async () => {
            setEditPosture(null);
            await loadVersion();
          }}
        />
      )}

      {editQuestion && (
        <EditQuestionDialog
          open={Boolean(editQuestion)}
          onOpenChange={(v) => { if (!v) setEditQuestion(null); }}
          schemaId={id!}
          vId={vId!}
          question={editQuestion}
          postureDomainId={postures.find((p) => p.id === editQuestion.postureId)?.domainId ?? null}
          domains={domains}
          onSaved={async () => {
            setEditQuestion(null);
            await loadVersion();
          }}
        />
      )}
    </>
  );
}
